import React from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Users, Award, Clock, CheckCircle, Shield, Lightbulb, Heart, Target } from "lucide-react";
import { companyInfo, getCompanyValues } from "../mock";
import { useTranslation } from "../hooks/useTranslation";
import { useLanguage } from "../contexts/LanguageContext";

const About = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  
  const companyValues = getCompanyValues(language);
  const timelineEvents = [
    {
      year: "2008",
      title: "Fondation d'AYMORENOV",
      description: "Création de l'entreprise avec une vision claire : offrir des services de rénovation de qualité supérieure."
    },
    {
      year: "2012",
      title: "Expansion de l'équipe",
      description: "Recrutement de spécialistes certifiés dans tous les domaines de la rénovation résidentielle."
    },
    {
      year: "2016",
      title: "Certification RBQ",
      description: "Obtention des certifications officielles et mise en place des standards de qualité les plus stricts."
    },
    {
      year: "2020",
      title: "Innovation technologique",
      description: "Intégration des dernières technologies en domotique et systèmes intelligents."
    },
    {
      year: "2024",
      title: "Plus de 1000 projets",
      description: "Franchissement du cap des 1000 projets réalisés avec plus de 800 clients satisfaits."
    }
  ];

  const teamMembers = [
    {
      name: "Alexandre Morenov",
      position: "Fondateur & PDG",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face",
      description: "Entrepreneur passionné avec plus de 20 ans d'expérience dans la construction et la rénovation."
    },
    {
      name: "Sarah Dubois",
      position: "Directrice des Opérations",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b47c?w=300&h=300&fit=crop&crop=face",
      description: "Experte en gestion de projets avec une approche client-centrée et une attention aux détails."
    },
    {
      name: "Michel Tremblay",
      position: "Chef d'Équipe Technique",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face",
      description: "Maître artisan avec une expertise reconnue dans tous les corps de métier de la rénovation."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6" style={{ color: "#283747" }}>
              {t('about.missionTitle')}
            </h1>
            <div className="text-lg text-slate-600 max-w-5xl mx-auto mb-8 text-left space-y-6">
              <p className="leading-relaxed">
                {t('about.missionIntro')}
              </p>
              
              <p className="leading-relaxed font-semibold" style={{ color: "#283747" }}>
                {t('about.missionStatement')}
              </p>
              
              <ul className="space-y-4 text-slate-700">
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span>{t('about.commitment1')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span>{t('about.commitment2')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span>{t('about.commitment3')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span>{t('about.commitment4')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ color: "#283747" }}>
              {t('about.valuesTitle')}
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              {t('about.valuesDescription')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {companyValues.map((value, index) => {
              const icons = [Shield, Award, Lightbulb, Heart];
              const Icon = icons[index];
              return (
                <Card key={index} className="text-center group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <CardContent className="p-8">
                    <div 
                      className="w-16 h-16 rounded-full flex items-center justify-center text-white mx-auto mb-6"
                      style={{ backgroundColor: "#D4A86A" }}
                    >
                      <Icon className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-bold mb-4" style={{ color: "#283747" }}>
                      {value.title}
                    </h3>
                    <p className="text-slate-600">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ backgroundColor: "#283747" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            {t('about.ctaTitle')}
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            {t('about.ctaDescription')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="font-semibold px-8 py-4 text-lg rounded-xl transition-all duration-300 hover:scale-105"
              style={{ backgroundColor: "#D4A86A", color: "#283747" }}
            >
              {t('about.freeQuoteButton')}
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              className="border-2 border-white text-white hover:bg-white px-8 py-4 text-lg rounded-xl transition-all duration-300"
              style={{ "--tw-hover-text": "#283747" }}
            >
              {t('about.viewRealizationsButton')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;